import { PeriodFilter } from 'types/GlobalWidget'
import { RollingType } from 'types/GlobalKpiOption'
import { ShowPerEnum, TMetricView } from 'types/GlobalMetric'

// Assemble the resolved metric view. The same merged filters, resolved period,
// and visible columns that the underlying-data fetch uses are passed in, so the
// table, charts, and chat are always describing exactly the same thing.
export const buildMetricView = (params: {
  metricId: string
  variantKpiTemplateId: string | null
  rollingSum: RollingType | null
  distribution: ShowPerEnum | null
  slices: string[]
  filters: Record<string, string[]>
  period: {
    period_filter: PeriodFilter | null
    from_date: string | null
    to_date: string | null
  }
  entityColumns: string[] | undefined
  title: string
  unit: string | null
}): TMetricView & { title: string; unit: string | null } => ({
  metric_id: params.metricId,
  variant_kpi_template_id: params.variantKpiTemplateId,
  rolling_sum: params.rollingSum,
  distribution: params.distribution,
  slices: params.slices,
  period_filter: params.period.period_filter,
  from_date: params.period.from_date,
  to_date: params.period.to_date,
  filters: params.filters,
  entity_columns: params.entityColumns,
  title: params.title,
  unit: params.unit
})

// Derive the ordered, visible entity_column_ids from a column-selection map —
// the same set the underlying-data table shows — so the chat matches it.
// Returns undefined when nothing is selected yet (use the metric's defaults).
export const visibleEntityColumns = (selection: {
  [columnId: string]: { id: string; index: number; hidden: boolean }
}): string[] | undefined => {
  const columns = Object.values(selection)

  if (columns.length === 0) return undefined

  return columns
    .filter((column) => !column.hidden)
    .sort((a, b) => a.index - b.index)
    .map((column) => column.id)
}

// Normalize a filter value (scalar, null, or array) into the string[] the chat
// metric view's `filters` map expects. `null` becomes the `'null'` sentinel the
// query params use to mean IS NULL.
export const filterValuesToStrings = (
  value: string | null | (string | number | null)[]
): string[] => {
  const values = Array.isArray(value) ? value : [value]

  return values.map(String)
}
